import { useEffect, useState } from 'react';

export type AppCapabilities = {
  camera: boolean;
  online: boolean;
  installed: boolean;
  storagePersisted: boolean;
  storageUsage: number;
  storageQuota: number;
};

const standaloneMedia = '(display-mode: standalone)';

export function useAppCapabilities(): AppCapabilities {
  const [capabilities, setCapabilities] = useState<AppCapabilities>(() => ({
    camera: Boolean(navigator.mediaDevices?.getUserMedia),
    online: navigator.onLine,
    installed: matchMedia(standaloneMedia).matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone),
    storagePersisted: false,
    storageUsage: 0,
    storageQuota: 0
  }));

  useEffect(() => {
    const media = matchMedia(standaloneMedia);
    const updateNetwork = () => setCapabilities((current) => ({ ...current, online: navigator.onLine }));
    const updateInstalled = () => setCapabilities((current) => ({ ...current, installed: media.matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone) }));
    const updateStorage = async () => {
      const [persisted, estimate] = await Promise.all([
        navigator.storage?.persisted?.().catch(() => false) ?? false,
        navigator.storage?.estimate?.().catch(() => ({ usage: 0, quota: 0 })) ?? { usage: 0, quota: 0 }
      ]);
      setCapabilities((current) => ({
        ...current,
        storagePersisted: persisted,
        storageUsage: estimate.usage ?? 0,
        storageQuota: estimate.quota ?? 0
      }));
    };

    addEventListener('online', updateNetwork);
    addEventListener('offline', updateNetwork);
    addEventListener('appinstalled', updateInstalled);
    media.addEventListener?.('change', updateInstalled);
    void updateStorage();
    const interval = setInterval(updateStorage, 30_000);
    return () => {
      removeEventListener('online', updateNetwork);
      removeEventListener('offline', updateNetwork);
      removeEventListener('appinstalled', updateInstalled);
      media.removeEventListener?.('change', updateInstalled);
      clearInterval(interval);
    };
  }, []);

  return capabilities;
}
